import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import { Close as CloseIcon, Save as SaveIcon, AutoAwesome as AIIcon } from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useFormAutoSave } from '../hooks/useFormAutoSave';
import { Highlight } from '../types';
import { api } from '../services/api';
import { useAuth } from 'react-oidc-context';

interface HighlightModalProps {
  highlight: Highlight | null;
  onSave: (highlight: Omit<Highlight, 'id'>) => void;
  onClose: () => void;
  saving?: boolean;
}

const HighlightModal: React.FC<HighlightModalProps> = ({ highlight, onSave, onClose, saving = false }) => {
  const { mode } = useTheme();
  const auth = useAuth();
  const preferredUsername = auth.user?.profile?.preferred_username || auth.user?.profile?.email || 'unknown-user';
  const [wordCount, setWordCount] = useState(0);
  const [aiSummarizing, setAiSummarizing] = useState(false);
  const [aiSummary, setAiSummary] = useState('');
  const [aiFeedback, setAiFeedback] = useState('');
  const [aiEnabled, setAiEnabled] = useState(true);

  // Initialize form data
  const initialFormData = {
    date: highlight ? format(new Date(highlight.date), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
    description: highlight ? highlight.description : '',
  };

  // Use auto-save hook
  const { formData, updateFormData, clearSavedData, hasUnsavedChanges } = useFormAutoSave({
    key: highlight ? `highlight-edit-${highlight.id}` : 'highlight-new',
    initialData: initialFormData,
    enabled: true,
  });

  useEffect(() => {
    // Update word count when description changes
    const words = formData.description
      .trim()
      .split(/\s+/)
      .filter((word: string) => word.length > 0);
    setWordCount(words.length);
  }, [formData.description]);

  useEffect(() => {
    // Check AI status on component mount
    const checkAiStatus = async () => {
      try {
        const status = await api.getAiStatus();
        setAiEnabled(status.ai_enabled);
      } catch (error) {
        console.error('Failed to check AI status:', error);
        setAiEnabled(false); // Default to disabled if check fails
      }
    };
    checkAiStatus();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      alias: preferredUsername,
      date: formData.date,
      description: formData.description,
      manager_approved: false,
      manager_alias: '', // Backend will set this from PAPI
    });
    // Clear auto-saved data after successful save
    clearSavedData();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    // Always update the form data to allow pasting
    // Validation will prevent submission if over limit
    updateFormData({ [name]: value });
  };

  const handleClose = () => {
    // Don't clear auto-saved data on close - let user recover it later
    onClose();
  };

  const handleAISummarize = async () => {
    if (!formData.description.trim()) {
      return;
    }

    setAiSummarizing(true);
    try {
      const response = await api.summarizeText(formData.description, 'highlight');
      setAiSummary(response.summary);
      setAiFeedback(response.feedback || '');
    } catch (error) {
      console.error('AI summarization failed:', error);
      // You could add a toast notification here
    } finally {
      setAiSummarizing(false);
    }
  };

  const handleAcceptChanges = () => {
    updateFormData({ description: aiSummary });
    setAiSummary('');
    setAiFeedback('');
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '400px',
          background:
            mode === 'dark'
              ? 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h6">{highlight ? 'Edit Highlight' : 'Add Highlight'}</Typography>
          {hasUnsavedChanges && (
            <Chip icon={<SaveIcon />} label="Auto-saved" size="small" color="primary" variant="outlined" />
          )}
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              type="date"
              label="Activity Date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              required
              InputLabelProps={{ shrink: true }}
            />

            {aiSummary && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {aiFeedback && (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: mode === 'dark' ? 'rgba(33, 150, 243, 0.1)' : 'rgba(33, 150, 243, 0.08)',
                      border: `1px solid ${mode === 'dark' ? 'rgba(33, 150, 243, 0.3)' : 'rgba(33, 150, 243, 0.2)'}`,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: mode === 'dark' ? '#64b5f6' : '#1976d2', fontWeight: 600, mb: 1 }}
                    >
                      Description Feedback
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      value={aiFeedback}
                      InputProps={{
                        readOnly: true,
                      }}
                      sx={{
                        '& .MuiInputBase-root': {
                          backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.7)',
                        },
                      }}
                    />
                  </Box>
                )}

                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: mode === 'dark' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.08)',
                    border: `1px solid ${mode === 'dark' ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.2)'}`,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography
                      variant="body2"
                      sx={{ color: mode === 'dark' ? '#81c784' : '#388e3c', fontWeight: 600 }}
                    >
                      Suggested Description
                    </Typography>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      onClick={handleAcceptChanges}
                      sx={{ ml: 'auto' }}
                    >
                      Accept Changes
                    </Button>
                  </Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={aiSummary}
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{
                      '& .MuiInputBase-root': {
                        backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.7)',
                      },
                    }}
                  />
                </Box>
              </Box>
            )}

            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Description ({wordCount}/250 words)
                </Typography>
                {aiEnabled && (
                  <Button
                    size="small"
                    startIcon={<AIIcon />}
                    onClick={handleAISummarize}
                    disabled={aiSummarizing || !formData.description.trim()}
                    sx={{
                      ml: 'auto',
                    }}
                    title="Generate AI summary of the description"
                  >
                    {aiSummarizing ? 'Summarizing...' : 'AI Summarize'}
                  </Button>
                )}
              </Box>
              <TextField
                fullWidth
                multiline
                rows={4}
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                placeholder="Describe your highlight..."
                error={wordCount > 250}
                helperText={wordCount > 250 ? 'Description exceeds 250 word limit' : ''}
              />
            </Box>

            {wordCount > 250 && <Alert severity="error">Description exceeds 250 word limit</Alert>}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={onClose} variant="outlined" color="secondary" disabled={saving}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={saving || wordCount > 250}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? 'Saving...' : highlight ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default HighlightModal;
